import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";

export interface AddToNotebookInput {
  item_type:
    | "doc"
    | "section"
    | "selection"
    | "chunk"
    | "asset"
    | "email"
    | "upload"
    | "generated"
    | "external_url"
    | "chat_answer"
    | "note";
  source_doc_id?: string;
  source_section_id?: string;
  source_version_id?: string;
  selected_text?: string;
  note_text?: string;
  title?: string;
}

interface ResolvedTarget {
  id: string;
  title: string;
}

const LAST_NOTEBOOK_KEY = "docustudio:last_notebook_id";

function readLocalLastNotebook(): string | null {
  try {
    return localStorage.getItem(LAST_NOTEBOOK_KEY);
  } catch {
    return null;
  }
}

function writeLocalLastNotebook(id: string): void {
  try {
    localStorage.setItem(LAST_NOTEBOOK_KEY, id);
  } catch {
    /* ignore quota / disabled storage */
  }
}

async function resolveTargetNotebook(): Promise<ResolvedTarget | null> {
  const localId = readLocalLastNotebook();
  const notebooks = await api.listNotebooks();
  if (localId) {
    const match = notebooks.items.find((n) => n.id === localId);
    if (match) return { id: match.id, title: match.title };
  }
  try {
    const { notebook_id } = await api.getLastNotebook();
    if (notebook_id) {
      const match = notebooks.items.find((n) => n.id === notebook_id);
      if (match) return { id: match.id, title: match.title };
    }
  } catch {
    /* server pref unavailable, fall through */
  }
  return null;
}

/**
 * useAddToNotebook returns mutations for the three explicit picker paths:
 *
 *   addToLast      — append to the user's last-used notebook (or default
 *                    "Mis apuntes" on first use). This is the keyboard ⌘N path.
 *
 *   addToNotebook  — append to a notebook chosen explicitly by id.
 *
 *   createAndAdd   — create a new notebook with the given title and append.
 *
 * Each path persists the chosen notebook as the new "last" both locally
 * (instant UX) and server-side (cross-device source of truth).
 */
export function useAddToNotebook() {
  const qc = useQueryClient();

  const lastNotebookQuery = useQuery({
    queryKey: ["last-notebook"],
    queryFn: () => api.getLastNotebook(),
    staleTime: 60_000,
  });

  function invalidate(notebookId: string): void {
    void qc.invalidateQueries({ queryKey: ["notebooks"] });
    void qc.invalidateQueries({ queryKey: ["notebook", notebookId] });
    void qc.invalidateQueries({ queryKey: ["last-notebook"] });
  }

  function persistLast(notebookId: string): void {
    writeLocalLastNotebook(notebookId);
    void api.setLastNotebook(notebookId).catch(() => {
      /* server failure is non-fatal; local wins */
    });
  }

  const addToLast = useMutation({
    mutationFn: async (item: AddToNotebookInput) => {
      let target = await resolveTargetNotebook();
      if (!target) {
        const created = await api.createNotebook({
          title: "Mis apuntes",
          scope: "personal",
        });
        target = { id: created.id, title: "Mis apuntes" };
      }
      const added = await api.addNotebookItem(target.id, item);
      return { notebookId: target.id, notebookTitle: target.title, itemId: added.id };
    },
    onSuccess: (result) => {
      persistLast(result.notebookId);
      invalidate(result.notebookId);
    },
  });

  const addToNotebook = useMutation({
    mutationFn: async ({ notebookId, item }: { notebookId: string; item: AddToNotebookInput }) => {
      const notebooks = await api.listNotebooks();
      const target = notebooks.items.find((n) => n.id === notebookId);
      if (!target) throw new Error("Notebook no encontrado");
      const added = await api.addNotebookItem(target.id, item);
      return { notebookId: target.id, notebookTitle: target.title, itemId: added.id };
    },
    onSuccess: (result) => {
      persistLast(result.notebookId);
      invalidate(result.notebookId);
    },
  });

  const createAndAdd = useMutation({
    mutationFn: async ({ name, item }: { name: string; item: AddToNotebookInput }) => {
      const created = await api.createNotebook({ title: name, scope: "personal" });
      const added = await api.addNotebookItem(created.id, item);
      return { notebookId: created.id, notebookTitle: name, itemId: added.id };
    },
    onSuccess: (result) => {
      persistLast(result.notebookId);
      invalidate(result.notebookId);
    },
  });

  return {
    addToLast,
    addToNotebook,
    createAndAdd,
    lastNotebookId: lastNotebookQuery.data?.notebook_id ?? readLocalLastNotebook(),
  };
}
